export interface OutlineScrollTrackerConfig {
  elements: NodeListOf<HTMLElement>;
  progressLine: HTMLElement | null;
  dotSelector: string;
  progressCssVar?: string;
  /**
   * When set, elements for which this returns true are excluded from the outline.
   * TODO: Used only by ungated variant to hide sidebar sections on desktop. Remove after.
   */
  isElementHidden?: (element: HTMLElement) => boolean;
}

interface OutlineElementData {
  outlineElement: HTMLElement;
  targetElement: HTMLElement | null;
  verticalPosition: number;
  dotPosition: number;
}

export interface OutlineScrollTracker {
  calculatePositions: () => void;
  updateProgress: () => void;
  updateProgressLinePadding: () => void;
  cleanup: () => void;
}

export const createOutlineScrollTracker = (
  config: OutlineScrollTrackerConfig,
): OutlineScrollTracker | null => {
  const { elements, progressLine, dotSelector, isElementHidden } = config;
  const progressCssVar = config.progressCssVar ?? '--progress-percentage';

  if (!elements.length) return null;

  const outlineElementData: OutlineElementData[] = [];

  const calculatePositions = () => {
    outlineElementData.length = 0;

    elements.forEach((outlineElement) => {
      // Ungated only: skip elements hidden on desktop (sidebar sections)
      if (isElementHidden?.(outlineElement)) {
        return;
      }
      const targetId = outlineElement.getAttribute('data-target-id');
      const targetElement = targetId ? document.getElementById(targetId) : null;

      if (!targetElement) {
        return;
      }

      const dot = outlineElement.querySelector(dotSelector) as HTMLElement;

      let dotPosition = 0;
      if (dot && progressLine) {
        const progressContainer = progressLine.parentElement;
        if (progressContainer) {
          let element: HTMLElement | null = dot;
          let offset = 0;
          let safety = 0;
          while (element && element !== progressContainer && safety < 100) {
            offset += element.offsetTop;
            element = element.offsetParent as HTMLElement;
            safety++;
          }
          dotPosition = offset;
        } else {
          dotPosition = dot.getBoundingClientRect().top + window.scrollY;
        }
      }

      const targetVerticalPosition =
        targetElement.getBoundingClientRect().top + window.scrollY;

      // Skip hidden/collapsed elements (position <= 0)
      if (targetVerticalPosition <= 0) {
        return;
      }

      outlineElementData.push({
        outlineElement,
        targetElement,
        verticalPosition: targetVerticalPosition,
        dotPosition,
      });
    });
  };

  const updateVerticalPositions = () => {
    outlineElementData.forEach((element) => {
      if (element.targetElement) {
        element.verticalPosition =
          element.targetElement.getBoundingClientRect().top + window.scrollY;
      }
    });
  };

  const updateProgress = () => {
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    let nextUnvisitedElementIndex = outlineElementData.length;
    let primaryElementIndex = 0;

    // Update styling: progress dots & visited state
    outlineElementData.forEach((element, index) => {
      if (element.targetElement) {
        const targetRect = element.targetElement.getBoundingClientRect();
        const isScrolledPast = scrollPosition >= element.verticalPosition;
        const isFullyVisible =
          targetRect.top >= 0 && targetRect.bottom <= windowHeight;

        const shouldBeVisited = isScrolledPast || isFullyVisible;

        if (shouldBeVisited) {
          element.outlineElement.classList.add('visited');
          primaryElementIndex = index;
        } else {
          element.outlineElement.classList.remove('visited');
          if (index < nextUnvisitedElementIndex) {
            nextUnvisitedElementIndex = index;
          }
        }
      }
    });

    // Set active class on the primary element
    outlineElementData.forEach((element, index) => {
      if (index === primaryElementIndex) {
        element.outlineElement.classList.add('primary');
      } else {
        element.outlineElement.classList.remove('primary');
      }
    });

    // Calculate progress percentage based on dot positions
    let progressPercentage = 0;
    if (nextUnvisitedElementIndex > 0) {
      const firstDotPosition = outlineElementData[0].dotPosition;
      const lastDotPosition =
        outlineElementData[outlineElementData.length - 1].dotPosition;
      const totalDistance = lastDotPosition - firstDotPosition;

      if (nextUnvisitedElementIndex < outlineElementData.length) {
        const lastVisitedElementIndex = nextUnvisitedElementIndex - 1;
        const currentDotPosition =
          outlineElementData[lastVisitedElementIndex].dotPosition;
        const nextDotPosition =
          outlineElementData[nextUnvisitedElementIndex].dotPosition;

        // Calculate base progress up to last visited dot
        const baseProgress =
          ((currentDotPosition - firstDotPosition) / totalDistance) * 100;

        // Calculate partial progress to next dot
        const distanceBetweenDots = nextDotPosition - currentDotPosition;
        const scrollPastCurrentDot = Math.max(
          0,
          scrollPosition -
            outlineElementData[lastVisitedElementIndex].verticalPosition,
        );
        const distanceBetweenTargets =
          outlineElementData[nextUnvisitedElementIndex].verticalPosition -
          outlineElementData[lastVisitedElementIndex].verticalPosition;
        const progressRatio = scrollPastCurrentDot / distanceBetweenTargets;
        const additionalProgress =
          (distanceBetweenDots / totalDistance) * progressRatio * 100;

        progressPercentage = Math.min(100, baseProgress + additionalProgress);
      } else {
        progressPercentage = 100;
      }
    }

    if (progressLine) {
      progressLine.style.setProperty(progressCssVar, `${progressPercentage}%`);
    }
  };

  const updateProgressLinePadding = () => {
    if (outlineElementData.length === 0 || !progressLine) return;

    // Use last visible element for padding (not full element list)
    const lastVisibleElement =
      outlineElementData[outlineElementData.length - 1].outlineElement;
    const lastElementHeight = lastVisibleElement.offsetHeight;
    const progressContainer = progressLine.parentElement;

    if (progressContainer) {
      progressContainer.style.paddingBottom = `${lastElementHeight - 5}px`;
    }
  };

  calculatePositions();

  if (progressLine) {
    progressLine.style.setProperty(progressCssVar, '0%');
  }

  const handleScroll = () => {
    updateVerticalPositions();
    updateProgress();
  };

  const handleResize = () => {
    updateVerticalPositions();
    updateProgress();
    updateProgressLinePadding();
  };

  const handleDocReady = () => {
    calculatePositions();
    updateProgressLinePadding();
    updateProgress();
  };

  // Listen for buildVuPageChanged events to recalculate when pages render
  const handleBuildVuPageChanged = () => {
    const prevCount = outlineElementData.length;
    calculatePositions();
    const newCount = outlineElementData.length;
    if (newCount > prevCount) {
      updateProgressLinePadding();
      updateProgress();
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize, { passive: true });
  window.addEventListener('docReady', handleDocReady);
  window.addEventListener('buildVuPageChanged', handleBuildVuPageChanged);

  updateProgressLinePadding();

  updateProgress();

  const cleanup = () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('docReady', handleDocReady);
    window.removeEventListener('buildVuPageChanged', handleBuildVuPageChanged);
  };

  return {
    calculatePositions,
    updateProgress,
    updateProgressLinePadding,
    cleanup,
  };
};
